import type { Report } from '@muzly/voice-engine';

import { readItem, removeItem, writeItem } from '../../core';

import type { CaptureSource } from './capture';
import type { FlowConfig, OnboardingAnswer } from './flow';

/**
 * storage — 진행 중인 플로우 **드래프트**의 로컬 영속화(client-only, 서버 없음).
 *
 * 플로우마다 키가 다르다(`flow.draftKey`) — 초기 설정 도중 이탈한 자리와 진단 회차의
 * 진행이 한 키에 있으면 한쪽을 이어 하려다 다른 쪽을 덮는다. 완료된 진단 회차는 여기
 * 없다 — `record/diagnosisStore`가 회차별 키로 소유한다. 프로필도 여기 없다 —
 * `features/profile/profile`이 정본이다.
 */

export interface OnboardingDraft {
  index: number;
  answers: Record<string, OnboardingAnswer>;
  report: Report | null;
  /** 그 리포트가 실측(native)인지 브라우저 mock인지 — 복원해도 표기가 유지돼야 한다. */
  reportSource: CaptureSource | null;
}

interface DraftStore {
  load: () => OnboardingDraft | null;
  save: (draft: OnboardingDraft) => void;
  clear: () => void;
}

/** 플로우 하나의 드래프트 저장소. 저장된 인덱스는 그 플로우의 단계 수 안으로 클램프한다. */
export const draftStore = (flow: FlowConfig): DraftStore => ({
  load: () => {
    const raw = readItem(flow.draftKey);
    if (!raw) {
      return null;
    }
    try {
      const draft = JSON.parse(raw) as Partial<OnboardingDraft>;
      // 단계 목록은 앱 버전 사이에 바뀔 수 있다 — 범위 밖 인덱스는 끝으로 접는다.
      const maxIndex = flow.steps.length - 1;
      return {
        index: Math.min(Math.max(0, draft.index ?? 0), maxIndex),
        answers: draft.answers ?? {},
        report: draft.report ?? null,
        reportSource: draft.reportSource ?? null,
      };
    } catch {
      return null;
    }
  },
  save: (draft) => writeItem(flow.draftKey, JSON.stringify(draft)),
  clear: () => removeItem(flow.draftKey),
});
